import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import AppIcon from './AppIcon';
import DeleteIcon from './DeleteIcon';
import './SubcontratadoDocumentos.css';

const MAX_FICHEIRO = 15 * 1024 * 1024;
const EXTENSOES = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'tif', 'tiff', 'bmp', 'doc', 'docx', 'odt'];
const nomeTipo = (tipo, tipos) => tipo === 'aptidao_medica'
    ? 'Ficha / Declaração de Aptidão Médica'
    : tipos.find(t => t.tipo === tipo)?.label || tipo;
const tamanho = bytes => bytes < 1024 * 1024 ? `${Math.max(1, Math.ceil(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
const chaveFicheiro = file => `${file.name}:${file.size}:${file.lastModified}`;

export function validarAnexosSubcontratado(anexos, tipos) {
    for (const anexo of anexos) {
        if (!tipos.some(t => t.tipo === anexo.tipo)) return `Escolha o tipo de documento para “${anexo.ficheiro.name}”.`;
        if (anexo.data_emissao && anexo.data_validade && anexo.data_validade < anexo.data_emissao) {
            return `A validade de “${anexo.ficheiro.name}” não pode ser anterior à data de emissão.`;
        }
    }
    return '';
}

export default function SubcontratadoDocumentos({ subcontratadoId, tipos, anexos, onChange, enviados, disabled }) {
    const picker = useRef(null);
    const sequencia = useRef(0);
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(Boolean(subcontratadoId));
    const [loadError, setLoadError] = useState('');
    const [fileError, setFileError] = useState('');
    const [openError, setOpenError] = useState('');
    const [revision, setRevision] = useState(0);

    useEffect(() => {
        if (!subcontratadoId) return;
        let cancelled = false;
        setLoading(true); setLoadError('');
        api.get(`/subcontratados/${subcontratadoId}/documentos`)
            .then(r => { if (!cancelled) setDocs(r.data || []); })
            .catch(() => { if (!cancelled) setLoadError('Não foi possível carregar os documentos existentes.'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [subcontratadoId, revision]);

    const adicionar = event => {
        const ficheiros = Array.from(event.target.files || []);
        event.target.value = '';
        const erros = [];
        const novos = [];
        const chaves = new Set(anexos.map(a => chaveFicheiro(a.ficheiro)));
        for (const ficheiro of ficheiros) {
            const extensao = ficheiro.name.split('.').pop().toLowerCase();
            if (!EXTENSOES.includes(extensao)) { erros.push(`“${ficheiro.name}”: utilize PDF, imagem ou Word.`); continue; }
            if (ficheiro.size === 0) { erros.push(`“${ficheiro.name}” está vazio.`); continue; }
            if (ficheiro.size > MAX_FICHEIRO) { erros.push(`“${ficheiro.name}” excede o limite de 15 MB.`); continue; }
            const chave = chaveFicheiro(ficheiro);
            if (chaves.has(chave)) { erros.push(`“${ficheiro.name}” já está na lista para anexar.`); continue; }
            chaves.add(chave);
            novos.push({ id: ++sequencia.current, ficheiro, tipo: '', data_emissao: '', data_validade: '', observacoes: '' });
        }
        onChange([...anexos, ...novos]);
        setFileError(erros.join(' '));
    };
    const atualizar = (id, campo, valor) => onChange(anexos.map(a => a.id === id ? { ...a, [campo]: valor } : a));
    const abrir = async doc => {
        setOpenError('');
        try {
            const r = await api.get(`/subcontratados/documento/${doc.id}/ficheiro`, { responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            window.open(url, '_blank', 'noopener,noreferrer');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch { setOpenError('Não foi possível abrir o documento. Tente novamente.'); }
    };
    // A resposta de cada envio é mantida mesmo se a leitura inicial ainda estiver a terminar.
    const guardados = Array.from(new Map([...docs, ...enviados].map(d => [d.id, d])).values());

    return <section className="subc-documents" aria-labelledby="subc-documents-heading">
        <div className="subc-documents-heading">
            <div>
                <h4 id="subc-documents-heading">Documentos do subcontratado</h4>
                <p>Seguros, ficha de aptidão médica / declaração e outros documentos.</p>
            </div>
            <button type="button" data-liquid-secondary="true" disabled={disabled || tipos.length === 0} onClick={() => picker.current?.click()}>
                <AppIcon name="upload" size={16} /> Adicionar documentos
            </button>
            <input ref={picker} type="file" multiple hidden accept={EXTENSOES.map(e => `.${e}`).join(',')}
                aria-label="Selecionar documentos" disabled={disabled} onChange={adicionar} />
        </div>
        <p className="subc-documents-hint">PDF, imagens ou Word · até 15 MB por ficheiro. Os anexos são enviados ao guardar.</p>
        {tipos.length === 0 && <p role="alert" className="subc-documents-error">Os tipos de documento estão indisponíveis. Feche e volte a abrir o formulário após atualizar a página.</p>}
        {fileError && <p role="alert" className="subc-documents-error">{fileError}</p>}
        {loading && <p role="status" className="subc-documents-hint">A carregar documentos existentes…</p>}
        {loadError && <div role="alert" className="subc-documents-error">{loadError} <button type="button" data-liquid-secondary="true" disabled={disabled} onClick={() => setRevision(v => v + 1)}>Tentar novamente</button></div>}
        {openError && <p role="alert" className="subc-documents-error">{openError}</p>}
        {guardados.length > 0 && <ul className="subc-documents-saved" aria-label="Documentos guardados">
            {guardados.map(doc => <li key={doc.id}>
                <AppIcon name="document" size={18} />
                <div className="subc-document-name">
                    <strong>{nomeTipo(doc.tipo, tipos)}</strong>
                    <span>{doc.nome_original}</span>
                    <small>{doc.data_validade ? `Validade: ${new Date(doc.data_validade).toLocaleDateString('pt-PT')}` : 'Sem validade indicada'}</small>
                </div>
                <button type="button" data-liquid-secondary="true" disabled={disabled} onClick={() => abrir(doc)} aria-label={`Abrir ${doc.nome_original}`}><AppIcon name="eye" size={16} /> Abrir</button>
            </li>)}
        </ul>}
        {anexos.length > 0 && <div className="subc-documents-queue" aria-label="Documentos por guardar">
            <p className="subc-documents-hint">{anexos.length} {anexos.length === 1 ? 'documento por guardar' : 'documentos por guardar'} · As datas são opcionais.</p>
            {anexos.map(anexo => <div className="subc-document-draft" key={anexo.id}>
                <div className="subc-document-top">
                    <AppIcon name="document" size={18} />
                    <div className="subc-document-name"><strong>{anexo.ficheiro.name}</strong><small>{tamanho(anexo.ficheiro.size)}</small></div>
                    <button type="button" data-liquid-secondary="true" disabled={disabled} onClick={() => onChange(anexos.filter(a => a.id !== anexo.id))} aria-label={`Remover ${anexo.ficheiro.name}`} title="Remover anexo"><DeleteIcon /></button>
                </div>
                <div className="subc-document-fields">
                    <div className="subc-document-type">
                        <label data-liquid-label="true" htmlFor={`subc-doc-${anexo.id}-tipo`}>Tipo de documento *</label>
                        <select id={`subc-doc-${anexo.id}-tipo`} required disabled={disabled} value={anexo.tipo} onChange={e => atualizar(anexo.id, 'tipo', e.target.value)}>
                            <option value="">Selecionar tipo…</option>
                            {tipos.map(t => <option key={t.tipo} value={t.tipo}>{nomeTipo(t.tipo, tipos)}</option>)}
                        </select>
                    </div>
                    <div>
                        <label data-liquid-label="true" htmlFor={`subc-doc-${anexo.id}-emissao`}>Data de emissão</label>
                        <input id={`subc-doc-${anexo.id}-emissao`} type="date" disabled={disabled} value={anexo.data_emissao} onChange={e => atualizar(anexo.id, 'data_emissao', e.target.value)} />
                    </div>
                    <div>
                        <label data-liquid-label="true" htmlFor={`subc-doc-${anexo.id}-validade`}>Válido até</label>
                        <input id={`subc-doc-${anexo.id}-validade`} type="date" disabled={disabled} min={anexo.data_emissao || undefined} value={anexo.data_validade} onChange={e => atualizar(anexo.id, 'data_validade', e.target.value)} />
                    </div>
                    <div className="subc-document-type">
                        <label data-liquid-label="true" htmlFor={`subc-doc-${anexo.id}-obs`}>Observações do documento</label>
                        <input id={`subc-doc-${anexo.id}-obs`} disabled={disabled} value={anexo.observacoes} placeholder="Ex.: nome do trabalhador ou número da apólice" onChange={e => atualizar(anexo.id, 'observacoes', e.target.value)} />
                    </div>
                </div>
            </div>)}
        </div>}
        {!loading && !loadError && guardados.length === 0 && anexos.length === 0 && <div className="subc-documents-empty"><AppIcon name="document" size={22} /><span>Ainda sem documentos. Pode selecionar vários ficheiros de uma vez.</span></div>}
    </section>;
}
